import React from 'react'
import classnames from 'classnames'
import PercentCell from './PercentCell'

export interface PercentBarCellProps {
  value: number | null | undefined
  digits?: number
  className?: string
  valueClassName?: string
  /** Tailwind classes for the filled portion of the bar. */
  barClassName?: string
  /** Scale ceiling for bar width; fill = min(value, max) / max. Default 100. */
  max?: number
  /** Accessible label for the bar (numeric % still shown alongside). */
  ariaLabel?: string
  /** Suffix for the default aria label, e.g. "of forecast" → "90.8% of forecast". */
  ariaDescription?: string
  /** Bar sits left of the value when right-aligned (default table columns). */
  align?: 'left' | 'right'
}

/** Fits "100.0" + muted % suffix at text-sm so bars share a vertical axis. */
const VALUE_WIDTH_CLASS = 'w-[3.5rem] shrink-0'

const roundPercent = (value: number, digits: number): number => {
  const scale = 10 ** digits
  const rounded = Math.round(value * scale) / scale
  return rounded === 0 ? 0 : rounded
}

/** Percent with a compact inline bar for at-a-glance comparison across rows. */
export const PercentBarCell: React.FC<PercentBarCellProps> = ({
  value,
  digits = 1,
  className,
  valueClassName,
  barClassName = 'bg-indigo-500 dark:bg-indigo-400',
  max = 100,
  ariaLabel,
  ariaDescription,
  align = 'right'
}) => {
  if (value == null || Number.isNaN(value)) {
    return <span className={className}>—</span>
  }

  const displayValue = roundPercent(value, digits)
  const safeMax = max > 0 ? max : 100
  const fillPct = Math.min(100, Math.max(0, (displayValue / safeMax) * 100))
  const defaultAriaLabel =
    ariaDescription != null && ariaDescription !== ''
      ? `${displayValue}% ${ariaDescription}`
      : `${displayValue}%`

  const bar = (
    <span
      className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700"
      role="img"
      aria-label={ariaLabel ?? defaultAriaLabel}
    >
      <span className={classnames('block h-full rounded-full', barClassName)} style={{ width: `${fillPct}%` }} />
    </span>
  )

  const valueEl = (
    <span className={classnames(VALUE_WIDTH_CLASS, align === 'right' ? 'text-right' : 'text-left')}>
      <PercentCell value={displayValue} digits={digits} valueClassName={valueClassName} />
    </span>
  )

  return (
    <span
      className={classnames(
        'inline-flex items-center gap-2',
        align === 'right' ? 'justify-end' : 'justify-start',
        className
      )}
    >
      {align === 'right' ? (
        <>
          {bar}
          {valueEl}
        </>
      ) : (
        <>
          {valueEl}
          {bar}
        </>
      )}
    </span>
  )
}

export default PercentBarCell
